import { istKeineWare } from "../categories/staples.js";
import type { CategoryKey } from "../categories/generated.js";
import type { Uuid } from "../types/base.js";
import type { MealPlanEntry, PantryItem, Recipe, RecipeIngredient, Unit } from "../types/entities.js";
import { addQuantities, dimensionOf, subtractStock, type Menge } from "../units/convert.js";

/**
 * Aus einer Woche wird ein Einkaufszettel.
 *
 * Drei Schritte, in dieser Reihenfolge:
 *
 * 1. Jedes geplante Essen bringt die Zutaten seines Rezepts mit, hochgerechnet
 *    auf die Personenzahl des Eintrags. Leitungswasser fällt dabei still weg,
 *    es ist keine Ware (siehe istKeineWare).
 * 2. Gleiche Zutaten werden zusammengezählt, getrennt nach dem, was sich
 *    verrechnen lässt (siehe units/convert).
 * 3. Der Vorrat wird abgezogen. Was übrig bleibt, kommt auf die Liste.
 *
 * Reine Berechnung, ohne Datenbank. Der Aufrufer schreibt das Ergebnis.
 */

export interface WeekPlanInput {
  entries: readonly MealPlanEntry[];
  recipes: readonly Recipe[];
  ingredients: readonly RecipeIngredient[];
  pantry: readonly PantryItem[];
  /** Nur diese Tage, als YYYY-MM-TT. Ohne Angabe zählt die ganze Woche. */
  days?: readonly string[];
}

/** Eine Zeile, die auf der Einkaufsliste landen soll. */
export interface PlannedItem {
  name: string;
  normalized_name: string;
  product_id: Uuid | null;
  category: CategoryKey | null;
  quantity: number | null;
  unit: Unit | null;
  /** Aus welchen Rezepten diese Zeile stammt, für die Vorschau. */
  recipes: string[];
}

export interface WeekPlanResult {
  /** Was fehlt und auf die Liste soll. */
  missing: PlannedItem[];
  /** Was der Vorrat schon abdeckt. Wird nur angezeigt, nicht angelegt. */
  covered: PlannedItem[];
  /** Zutaten, die als immer vorhanden markiert sind. */
  staples: string[];
}

interface Sammlung {
  name: string;
  normalized_name: string;
  product_id: Uuid | null;
  category: CategoryKey | null;
  mengen: Menge[];
  recipes: Set<string>;
}

export function planWeekToList(input: WeekPlanInput): WeekPlanResult {
  const rezepte = new Map(input.recipes.filter((r) => r.deleted_at === null).map((r) => [r.id, r]));
  const zutatenJeRezept = new Map<Uuid, RecipeIngredient[]>();
  for (const zutat of input.ingredients) {
    if (zutat.deleted_at !== null) continue;
    const bisher = zutatenJeRezept.get(zutat.recipe_id) ?? [];
    bisher.push(zutat);
    zutatenJeRezept.set(zutat.recipe_id, bisher);
  }

  const gesammelt = new Map<string, Sammlung>();
  const staples = new Set<string>();

  for (const eintrag of input.entries) {
    if (eintrag.deleted_at !== null || eintrag.recipe_id === null) continue;
    if (input.days && !input.days.includes(eintrag.day)) continue;

    const rezept = rezepte.get(eintrag.recipe_id);
    if (!rezept) continue;

    // Ein Rezept für zwei, geplant für vier: alle Mengen mal zwei. Die
    // Personenzahl eines Rezepts ist nie null, das sichert die Migration.
    const faktor = (eintrag.servings ?? rezept.servings) / rezept.servings;

    for (const zutat of zutatenJeRezept.get(rezept.id) ?? []) {
      // Leitungswasser ist keine Ware. Es wird nicht einmal als "habe ich
      // schon" gemeldet: Die Zeile "Immer im Haus: Wasser" erklärt etwas,
      // das niemand erklärt haben wollte.
      if (istKeineWare(zutat.name)) continue;

      if (zutat.is_staple) {
        staples.add(zutat.name);
        continue;
      }

      const schluessel = zutat.product_id ?? zutat.normalized_name;
      const stand = gesammelt.get(schluessel) ?? {
        name: zutat.name,
        normalized_name: zutat.normalized_name,
        product_id: zutat.product_id,
        category: zutat.category,
        mengen: [],
        recipes: new Set<string>(),
      };

      stand.mengen.push({
        quantity: zutat.quantity === null ? null : zutat.quantity * faktor,
        unit: zutat.unit,
      });
      stand.recipes.add(rezept.name);
      // Eine Kategorie aus irgendeinem Rezept ist besser als keine.
      stand.category ??= zutat.category;
      gesammelt.set(schluessel, stand);
    }
  }

  const vorrat = vorratNachSchluessel(input.pantry);
  const missing: PlannedItem[] = [];
  const covered: PlannedItem[] = [];

  for (const [schluessel, stand] of gesammelt) {
    const summen = addQuantities(stand.mengen);
    const vorhanden = vorrat.get(schluessel) ?? [];

    for (const summe of summen) {
      const rest = abzug(summe, vorhanden);
      const zeile: PlannedItem = {
        name: stand.name,
        normalized_name: stand.normalized_name,
        product_id: stand.product_id,
        category: stand.category,
        quantity: (rest ?? summe).quantity,
        unit: (rest ?? summe).unit,
        recipes: [...stand.recipes],
      };
      if (rest === null) covered.push(zeile);
      else missing.push(zeile);
    }
  }

  return {
    missing: missing.sort(nachName),
    covered: covered.sort(nachName),
    staples: [...staples].sort((a, b) => a.localeCompare(b, "de-DE")),
  };
}

function nachName(a: PlannedItem, b: PlannedItem): number {
  return a.name.localeCompare(b.name, "de-DE");
}

/** Vorrat nach Produkt oder Name, damit der Abzug ihn findet. */
function vorratNachSchluessel(pantry: readonly PantryItem[]): Map<string, Menge[]> {
  const karte = new Map<string, Menge[]>();
  for (const zeile of pantry) {
    if (zeile.deleted_at !== null) continue;
    for (const schluessel of [zeile.product_id, zeile.normalized_name]) {
      if (schluessel === null) continue;
      const bisher = karte.get(schluessel) ?? [];
      bisher.push({ quantity: zeile.quantity, unit: zeile.unit });
      karte.set(schluessel, bisher);
    }
  }
  return karte;
}

/** Zieht den passenden Teil des Vorrats ab. Was nicht passt, bleibt liegen. */
function abzug(gebraucht: Menge, vorrat: readonly Menge[]): Menge | null {
  const passend = vorrat.filter((v) => dimensionOf(v.unit) === dimensionOf(gebraucht.unit));
  if (passend.length === 0) return gebraucht;

  const zusammen = addQuantities(passend)[0];
  return subtractStock(gebraucht, zusammen ?? { quantity: 0, unit: gebraucht.unit });
}
